import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { client } from "@augmodo/lebron-browser-client";
import type {
  BenchmarkJobCreateSchema,
  BenchmarkJobSchema,
} from "../generated/types.gen";

/** One quiz row. lebron returns this same shape from /mine and from create.
 *
 * `id` is the job sid — quiz rows link to the play-mode route for the quiz's
 * own task type (see TASK_TYPE_ROUTE / BENCHMARK_JOB_TYPE_ROUTE in
 * @/config/data), not always /confirmation.
 *
 * Types come from the generated client, `client` from the published browser
 * client (it carries the app's auth), same split as useImageGallery. The URLs
 * below stay written out for that reason: the generated SDK functions bind to
 * their own unauthenticated client.
 *
 * `type` is hand-added rather than pulled from the generated `BenchmarkJobSchema`:
 * it lands there only once `npm run generate` runs against a lebron that
 * serves it (mvp/schemas.py). Swap for the generated field then.
 */
export type BenchmarkQuiz = BenchmarkJobSchema & {
  /** The JobType this quiz's tasks carry, e.g.
   * "OUT_OF_STOCK_CONFIRMATION_BENCHMARK_TASKS" or
   * "COVERAGE_REVIEW_BENCHMARK_TASKS". */
  type: string;
};

/** One quiz scored against its gold answers.
 *
 * Hand-written rather than imported from `../generated/types.gen`: the generator
 * reads lebron-stage's live spec, so `BenchmarkJobMetricsSchema` only appears
 * there once the endpoint is deployed. Swap this alias for the generated type
 * after the next `npm run generate`.
 *
 * `answered` counts completed questions only, so `total_questions - answered` is
 * the skipped remainder. `precision` is 0..1. Every derived metric is nullable —
 * an unstarted or fully-skipped quiz has no precision and no pace.
 */
export interface BenchmarkQuizMetrics {
  id: string;
  reviewer_email: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  store_name: string;
  total_questions: number;
  answered: number;
  correct: number;
  precision: number | null;
  avg_seconds_per_task: number | null;
  tasks_per_hour: number | null;
}

/** Thrown by useCreateBenchmarkQuizzes. `unknownEmails` carries lebron's
 * structured list of entries with no signed-in account, so the form can flag
 * the exact pills rather than only showing the message. */
export class CreateQuizzesError extends Error {
  unknownEmails: string[];

  constructor(message: string, unknownEmails: string[] = []) {
    super(message);
    this.unknownEmails = unknownEmails;
  }
}

function unknownEmailsFrom(error: unknown): string[] {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (!Array.isArray(detail)) return [];
  return detail.flatMap(
    (d) => (d as { unknown_emails?: string[] })?.unknown_emails ?? [],
  );
}

/** Ninja returns `{detail: "..."}` for auth errors and `{detail: [{msg}]}` for
 * validation errors (e.g. n_questions exceeding the candidate pool). Both carry
 * a message worth showing the admin verbatim. */
function serverMessage(error: unknown, fallback: string): string {
  const detail = (error as { detail?: unknown } | undefined)?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (d as { msg?: string })?.msg)
      .filter((m): m is string => !!m);
    if (msgs.length) return msgs.join("; ");
  }
  return fallback;
}

/** Query key for the caller's own quizzes. Exported so the flows that change
 * this list — taking a quiz, creating one — can invalidate it by name. */
export const MY_BENCHMARK_QUIZZES_KEY = ["benchmark-quizzes-mine"];

/**
 * The caller's own benchmark quizzes, every status, newest first.
 *
 * Scoped server-side from the Firebase identity — there are no query params and
 * no client-side email filter. Unpaginated: one reviewer has few quizzes.
 *
 * Always refetched on mount rather than inheriting the app-wide 5-minute
 * staleTime: a reviewer arrives here straight from finishing a quiz, and a list
 * that still says "Needs Review" reads as the submission having been lost. The
 * list is small and unpaginated, so the extra request is cheap.
 */
export function useMyBenchmarkQuizzes(enabled = true) {
  return useQuery({
    queryKey: MY_BENCHMARK_QUIZZES_KEY,
    queryFn: async (): Promise<BenchmarkQuiz[]> => {
      const response = await client.get({
        url: "/v1/qc-benchmarks/jobs/mine",
      });

      if (response.error) {
        throw new Error(
          serverMessage(response.error, "Failed to fetch benchmark quizzes"),
        );
      }

      return response.data as BenchmarkQuiz[];
    },
    enabled,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/**
 * One quiz's results, scored server-side against the gold answers.
 *
 * lebron scopes this to the quiz's own reviewer, plus staff and superusers — so
 * a caller naming someone else's quiz id gets a 401 rather than their results.
 *
 * Pass `null` to disable (nothing is fetched until a quiz is actually selected).
 *
 * Never served from cache: a quiz's score changes as it's answered, so the
 * app-wide 5-minute staleTime would let a snapshot taken mid-quiz reappear as
 * the final result. Callers are the completion screen and the My Quizzes modal,
 * both of which want the score as it stands right now.
 */
export function useBenchmarkQuizMetrics(jobId: string | null) {
  return useQuery({
    queryKey: ["benchmark-quiz-metrics", jobId],
    staleTime: 0,
    refetchOnMount: "always",
    queryFn: async (): Promise<BenchmarkQuizMetrics> => {
      const response = await client.get({
        url: `/v1/qc-benchmarks/jobs/${jobId}/metrics`,
      });

      if (response.error) {
        throw new Error(
          serverMessage(response.error, "Failed to load quiz results"),
        );
      }

      return response.data as BenchmarkQuizMetrics;
    },
    enabled: !!jobId,
  });
}

/** The QC task families a quiz can be created for. A subset of the generated
 * `JobTaskType` union (checked with `Extract`, so a renamed or dropped value
 * fails the build instead of the request) — lebron's `_BENCHMARK_FAMILIES`
 * registry (mvp/benchmark.py) is the source of truth for which task types
 * actually have a benchmark family; this must stay in sync with it. */
export type BenchmarkableTaskType = Extract<
  BenchmarkJobCreateSchema["task_type"],
  "OUT_OF_STOCK_CONFIRMATION" | "COVERAGE_REVIEW"
>;

/** Create one quiz of `n_questions` sampled gold tasks per email. Staff only. */
export function useCreateBenchmarkQuizzes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      task_type: BenchmarkableTaskType;
      emails: string[];
      n_questions: number;
    }): Promise<BenchmarkQuiz[]> => {
      const body: BenchmarkJobCreateSchema = payload;
      const response = await client.post({
        url: "/v1/qc-benchmarks/jobs",
        body,
      });

      if (response.error) {
        throw new CreateQuizzesError(
          serverMessage(response.error, "Failed to create benchmark quizzes"),
          unknownEmailsFrom(response.error),
        );
      }

      return response.data as BenchmarkQuiz[];
    },
    // An admin creating a quiz for themselves should see it on My Quizzes
    // immediately. Switching tabs there doesn't remount the jobs table — it's
    // the same route with a different ?tab — so refetchOnMount never fires and
    // the list would sit stale until the next poll. The list query is active
    // even while the admin tab is showing, so this refetches it right away.
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_BENCHMARK_QUIZZES_KEY });
    },
  });
}
